import React, { useState, useRef } from 'react';
import { 
  Settings, 
  Power, 
  FileText, 
  MessageSquare, 
  Users, 
  BarChart3,
  X,
  Send
} from 'lucide-react';
import { collection, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';

const endClassForEveryone = httpsCallable(functions, 'endClassForEveryone');
const transferHost = httpsCallable(functions, 'transferHost');

const HostControls = ({ 
  classId, 
  participants, 
  currentUserId, 
  chatMode, 
  onChatModeChange
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const fileInputRef = useRef(null);

  const handleEndClass = async () => {
    if (!window.confirm('Are you sure you want to end the class for everyone? This will disconnect all participants.')) {
      return;
    }

    try {
      await endClassForEveryone({ classId });
      toast.success('Class ended for all participants');
    } catch (error) {
      console.error('Error ending class:', error);
      toast.error('Failed to end class');
    }
  };

  const handleTransferHost = async (newHostId, newHostName) => {
    if (!window.confirm(`Transfer host privileges to ${newHostName}?`)) {
      return;
    }

    try {
      await transferHost({ classId, newHostId });
      toast.success(`Host privileges transferred to ${newHostName}`);
    } catch (error) {
      console.error('Error transferring host:', error);
      toast.error('Failed to transfer host');
    }
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('PDF file size must be less than 10MB');
      return;
    }

    try {
      setUploadingPDF(true);
      const result = await uploadFile(file, `live-class-pdfs/${classId}/${Date.now()}_${file.name}`);
      const pdfUrl = result.url || result;
      
      // Send PDF link in chat
      const messagesRef = collection(db, 'webrtcSessions', classId, 'chat');
      await addDoc(messagesRef, {
        userId: currentUserId,
        userName: 'Host',
        message: `📄 Shared PDF: ${file.name}`,
        pdfUrl: pdfUrl,
        pdfName: file.name,
        messageType: 'pdf',
        timestamp: serverTimestamp()
      });

      toast.success('PDF shared in chat');
      fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading PDF:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingPDF(false);
    }
  };

  const handleCreatePoll = async () => {
    if (!pollQuestion.trim()) {
      toast.error('Please enter a poll question');
      return;
    }

    const validOptions = pollOptions.filter(opt => opt.trim());
    if (validOptions.length < 2) {
      toast.error('Please provide at least 2 options');
      return;
    }

    try {
      const pollsRef = collection(db, 'webrtcSessions', classId, 'polls');
      await addDoc(pollsRef, {
        question: pollQuestion.trim(),
        options: validOptions.map(opt => ({ text: opt.trim(), votes: 0, voters: [] })),
        createdBy: currentUserId,
        status: 'active',
        createdAt: serverTimestamp()
      });

      // Notify in chat
      const messagesRef = collection(db, 'webrtcSessions', classId, 'chat');
      await addDoc(messagesRef, {
        userId: currentUserId,
        userName: 'Host',
        message: `📊 New poll: ${pollQuestion.trim()}`,
        messageType: 'poll_created',
        timestamp: serverTimestamp()
      });

      setPollQuestion('');
      setPollOptions(['', '']);
      setShowPollModal(false);
      toast.success('Poll created!');
    } catch (error) {
      console.error('Error creating poll:', error);
      toast.error('Failed to create poll');
    }
  };

  const addPollOption = () => {
    setPollOptions([...pollOptions, '']);
  };

  const removePollOption = (index) => {
    setPollOptions(pollOptions.filter((_, i) => i !== index));
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 hover:bg-gray-700 rounded flex items-center space-x-2"
          title="Host Controls"
        >
          <Settings size={20} />
        </button>

        {showMenu && (
          <div className="absolute right-0 top-full mt-2 w-64 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-50">
            <div className="p-2 space-y-1">
              <button
                onClick={handleEndClass}
                className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded flex items-center space-x-2 text-red-400"
              >
                <Power size={16} />
                <span>End Class for Everyone</span>
              </button>

              <label className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded flex items-center space-x-2 cursor-pointer">
                <FileText size={16} />
                <span>Share PDF</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handlePDFUpload}
                  className="hidden"
                  disabled={uploadingPDF}
                />
              </label>

              <button
                onClick={() => onChatModeChange(chatMode === 'everyone' ? 'host' : 'everyone')}
                className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded flex items-center space-x-2"
              >
                <MessageSquare size={16} />
                <span>Chat: {chatMode === 'everyone' ? 'Everyone' : 'Host Only'}</span>
              </button>

              <button
                onClick={() => setShowPollModal(true)}
                className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded flex items-center space-x-2"
              >
                <BarChart3 size={16} />
                <span>Create Poll</span>
              </button>

              <div className="border-t border-gray-700 my-1"></div>
              <div className="px-4 py-2 text-xs text-gray-400">Transfer Host To:</div>
              {participants
                .filter(p => p.userId !== currentUserId)
                .map((participant) => (
                  <button
                    key={participant.userId}
                    onClick={() => handleTransferHost(participant.userId, participant.userName)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-700 rounded flex items-center space-x-2"
                  >
                    <Users size={16} />
                    <span>{participant.userName}</span>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Poll Creation Modal */}
      {showPollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Create Poll</h3>
              <button
                onClick={() => setShowPollModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Poll Question
                </label>
                <input
                  type="text"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  placeholder="Enter your question..."
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Options
                </label>
                {pollOptions.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updatePollOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {pollOptions.length > 2 && (
                      <button
                        onClick={() => removePollOption(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addPollOption}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  + Add Option
                </button>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setShowPollModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePoll}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
                >
                  <Send size={16} />
                  <span>Create Poll</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HostControls;

